import React, { useState, useEffect } from "react";
import {
    Accordion, AccordionSummary, AccordionDetails,
    Typography, Grid, Box, TextField, IconButton, CircularProgress,
    Button
} from "@mui/material";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CheckIcon from "@mui/icons-material/Check";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import NakdanButton from "../../SharedArea/NakdanButton";

import { observer } from "mobx-react-lite";
import additionalInfo from "../../../Mobx/AdditionalInfo";
import audio from "../../../Utils/audioPlayer";

const ExistsSentenceSection = observer(() => {
    const existsInfo = additionalInfo.existsInfo;
    const expanded = additionalInfo.expandedState;
    const loading = additionalInfo.loading;

    const [editedSentences, setEditedSentences] = useState({});

    useEffect(() => {
        if (existsInfo?.sentences) {
            setEditedSentences(
                existsInfo.sentences.reduce((acc, sentence) => {
                    acc[sentence._id] = sentence.text;
                    return acc;
                }, {})
            );
        }
    }, [existsInfo]);

    const handleToggle = () => {
        additionalInfo.setExpandedState({ ...expanded, sentences: !expanded.sentences });
    };

    const addFullStop = (text) => (!/[,;:.!?]$/.test(text.trim())) ? text + '.' : text;
    const isHebrew = (text) => /^[\u0590-\u05FF]/.test(text.trim());
    const playAudioFile = (fileName) => {
        const src = `https://lexillon-files-il.s3.il-central-1.amazonaws.com/files/audio/additional-info/${fileName}`;
        audio.pause();
        audio.src = src;
        audio.play();
    };

    return (
        <Grid item xs={12}>
            <Accordion
                expanded={expanded.sentences}
                onChange={() => handleToggle("sentences")}
                sx={{ boxShadow: 3, borderRadius: 2, width: "100%" }}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">({existsInfo.sentences?.length || 0}) משפטים</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ width: "100%", textAlign: "center" }}>
                    {/* כפתור "מחק הכל" */}
                    <Button
                        variant="outlined"
                        color="error"
                        onClick={() =>
                            additionalInfo.clearExistsInfoField(
                                existsInfo._id,
                                "sentences"
                            )
                        }
                        sx={{ mb: 2, ml: 1, minWidth: 81, minHeight: 36 }}
                        disabled={loading.updateExistsInfo.sentences?.clear}
                    >
                        {loading.updateExistsInfo.sentences?.clear ? (
                            <CircularProgress size={16} />
                        ) : (
                            "מחק הכל"
                        )}
                    </Button>
                    <Box sx={{ width: "100%", textAlign: "center" }}>
                        {existsInfo.sentences?.length > 0 ? (
                            existsInfo.sentences.map((sentence) => {
                                const text = editedSentences[sentence._id] || "";
                                const lang = isHebrew(text) ? "heIL" : "enUS";
                                const audioData = sentence.audio?.[lang] || {};
                                const maleFile = audioData.male;
                                const femaleFile = audioData.female;

                                return (
                                    <Box
                                        key={sentence._id}
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            bgcolor: "#f0f0f0",
                                            p: 1,
                                            pt: 0,
                                            borderRadius: 2,
                                            mt: 0.5
                                        }}
                                    >
                                        <Box sx={{ display: "flex", gap: 0.5, ml: 1 }}>
                                            {/* כפתורי אודיו */}
                                            <IconButton
                                                onClick={() => maleFile && playAudioFile(maleFile)}
                                                disabled={!maleFile}
                                            >
                                                <VolumeUpIcon titleAccess="איש" />
                                            </IconButton>
                                            <IconButton
                                                onClick={() => femaleFile && playAudioFile(femaleFile)}
                                                disabled={!femaleFile}
                                            >
                                                <VolumeUpIcon titleAccess="אשה" />
                                            </IconButton>

                                            {/* כפתור ניקוד */}
                                            <NakdanButton
                                                text={text}
                                                onNikudComplete={(nikudText) => {
                                                    setEditedSentences(prev => ({
                                                        ...prev,
                                                        [sentence._id]: nikudText,
                                                    }));
                                                }}
                                            />

                                            {/* כפתורי ברירת מחדל / שמירה / מחיקה */}
                                            {sentence.default ? (
                                                <Typography variant="body2" color="primary" sx={{ fontWeight: "bold", mt: 1.3, mr: 1 }}>
                                                    ברירת מחדל
                                                </Typography>
                                            ) : (
                                                <IconButton
                                                    onClick={() =>
                                                        additionalInfo.updateExistsInfo(
                                                            existsInfo._id,
                                                            "sentences",
                                                            sentence._id,
                                                            { default: true }
                                                        )
                                                    }
                                                    disabled={loading.updateExistsInfo.sentences?.[sentence._id] === "default"}
                                                >
                                                    {loading.updateExistsInfo.sentences?.[sentence._id] === "default"
                                                        ? <CircularProgress size={24} />
                                                        : <CheckIcon color="primary" />}
                                                </IconButton>
                                            )}
                                            <IconButton
                                                onClick={() =>
                                                    additionalInfo.updateExistsInfo(
                                                        existsInfo._id,
                                                        "sentences",
                                                        sentence._id,
                                                        { text: editedSentences[sentence._id] }
                                                    )
                                                }
                                                disabled={loading.updateExistsInfo.sentences?.[sentence._id] === "text"}
                                            >
                                                {loading.updateExistsInfo.sentences?.[sentence._id] === "text"
                                                    ? <CircularProgress size={24} />
                                                    : <SaveIcon color="primary" />}
                                            </IconButton>
                                            <IconButton
                                                onClick={() =>
                                                    additionalInfo.deleteExistsInfo(
                                                        existsInfo._id,
                                                        "sentences",
                                                        sentence._id
                                                    )
                                                }
                                                disabled={loading.deleteExistsInfo.sentences?.[sentence._id]}
                                            >
                                                {loading.deleteExistsInfo.sentences?.[sentence._id]
                                                    ? <CircularProgress size={24} />
                                                    : <DeleteIcon color="error" />}
                                            </IconButton>
                                        </Box>

                                        <TextField
                                            fullWidth
                                            variant="outlined"
                                            size="small"
                                            multiline
                                            minRows={1}
                                            value={text}
                                            onChange={(e) =>
                                                setEditedSentences({
                                                    ...editedSentences,
                                                    [sentence._id]: e.target.value,
                                                })
                                            }
                                            sx={{
                                                direction: isHebrew(text) ? "ltr" : "rtl"
                                            }}
                                        />
                                    </Box>
                                );
                            })
                        ) : (
                            <Typography variant="body2" color="textSecondary">אין משפטים זמינים</Typography>
                        )}
                    </Box>
                </AccordionDetails>
            </Accordion>
        </Grid>
    );
});

export default ExistsSentenceSection;
